import React, { useState, useEffect } from 'react';
import { AppContext } from '../../context/AppContext';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
  CCard,
  CCardBody,
  CContainer,
  CCol,
  CRow,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CButton,
  CAlert,
  CSpinner,
  CFormFeedback,
  CInputGroup,
  CInputGroupText,
  CFormCheck
} from '@coreui/react';
import { CustomBreadcrumbs } from '../../components';
import { useNavigate } from 'react-router-dom';

// --- VALIDACIÓN (Igual para crear y editar) ---
const validationSchema = Yup.object({
  email: Yup.string().required('El email es obligatorio').email('Formato inválido'),
  // En edición, la contraseña es opcional. La ajustaremos dinámicamente en el componente si es necesario,
  // pero por ahora la dejamos requerida y el padre puede limpiarla si no se envía.
  password: Yup.string().min(6, 'Mínimo 6 caracteres'), 
  company_name: Yup.string().required('Requerido').min(2).max(100),
  owner_name: Yup.string().required('Requerido').min(3).max(100),
  company_category_id: Yup.string().required('Requerido'),
  subscription_plan_id: Yup.string().required('Requerido'),
  subscription_status_id: Yup.string().required('Requerido'),
  phone_country_code: Yup.string().nullable().matches(/^\+[1-9]\d{0,2}$/, 'Inválido'),
  phone_area_code: Yup.string().nullable().matches(/^\d{2,5}$/, '2-5 dígitos'),
  phone_number: Yup.string().nullable().matches(/^\d{6,10}$/, '6-10 dígitos'),
  billing_name: Yup.string().nullable().min(2).max(100),
  billing_cuit: Yup.string().nullable().min(7).max(11),
  billing_email: Yup.string().nullable().email('Inválido'),
  billing_phone_country_code: Yup.string().nullable().matches(/^\+[1-9]\d{0,2}$/, 'Inválido'),
  billing_phone_area_code: Yup.string().nullable().matches(/^\d{2,5}$/, '2-5 dígitos'),
  billing_phone_number: Yup.string().nullable().matches(/^\d{6,10}$/, '6-10 dígitos'),
  company_tax_status_id: Yup.string().nullable(),
});

const FormCompany = ({ 
  mode = 'create', // 'create' | 'edit'
  initialData = null, // Datos de la empresa si es edición
  onSubmitFunc, // Función del contexto (createCompany o updateCompany)
  pageTitle,
  breadcrumbTitle 
}) => {
    const navigate = useNavigate();
    const [isSubmittingGlobal, setIsSubmittingGlobal] = useState(false);
    const [apiError, setApiError] = useState(null);
    const [apiSuccess, setApiSuccess] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    
    // States para selects
    const [companyCategories, setCompanyCategories] = useState([]);
    const [companySubscriptionPlans, setCompanySubscriptionPlans] = useState([]);
    const [companySubscriptionStatuses, setCompanySubscriptionStatuses] = useState([]);
    const [companyTaxStatuses, setCompanyTaxStatuses] = useState([]);
    const [loadingOptions, setLoadingOptions] = useState(true);

    const apiSuccessMessage = mode === 'create' ? 'Empresa creada exitosamente. Redirigiendo...' : 'Empresa actualizada exitosamente. Redirigiendo...';

    const { 
        getCompanySubscriptionPlans, 
        getCompanySubscriptionStatuses, 
        getCompanyTaxStatuses,
        getCompanyCategoriesForCreation
    } = React.useContext(AppContext);

    // Cargar opciones de selects
    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const [plansRes, statusesRes, taxRes, catsRes] = await Promise.all([
                    getCompanySubscriptionPlans(), 
                    getCompanySubscriptionStatuses(), 
                    getCompanyTaxStatuses(),
                    getCompanyCategoriesForCreation()
                ]);
                setCompanySubscriptionPlans(plansRes.data || plansRes);
                setCompanySubscriptionStatuses(statusesRes.data || statusesRes);
                setCompanyTaxStatuses(taxRes.data || taxRes);
                setCompanyCategories(catsRes.data || catsRes);
                setLoadingOptions(false);
            } catch (error) {
                console.error("Error cargando opciones", error);
                setLoadingOptions(false);
            }
        };
        fetchOptions();
    }, [getCompanySubscriptionPlans, getCompanySubscriptionStatuses, getCompanyTaxStatuses, getCompanyCategoriesForCreation]);

    const handleSubmit = async (values, { setSubmitting, resetForm, setErrors }) => {
        setIsSubmittingGlobal(true);
        setApiError(null);

        try {
            // Si es edición y no se envió contraseña, la quitamos del payload para no enviar null/vacío al backend
            let payload = { ...values };
            if (mode === 'edit' && (!payload.password || payload.password.trim() === '')) {
                delete payload.password;
            }

            await onSubmitFunc(payload);

            setApiSuccess(apiSuccessMessage);

            if (mode === 'create') {
                resetForm();
            }
            
            // Éxito
            setTimeout(() => {
                navigate("/dashboard/companies");
            }, 1000);
            
        } catch (error) {
            console.log(error);
            if (error.response?.status === 422 && error.response?.data?.errors) {
                const apiErrors = {};
                Object.keys(error.response.data.errors).forEach((key) => {
                    apiErrors[key] = error.response.data.errors[key][0];
                });
                setErrors(apiErrors);
            } else {
                setApiError(error.response?.data?.message || 'Ocurrió un error inesperado.');
            }
        } finally {
            setSubmitting(false);
            setIsSubmittingGlobal(false);
        }
    };

    // Valores iniciales combinados (default + data de edición si existe)
    const defaultValues = {
        email: '', password: '', company_name: '', owner_name: '',
        company_category_id: '', subscription_plan_id: '', subscription_status_id: '',
        phone_country_code: '+', phone_area_code: '', phone_number: '',
        billing_name: '', billing_cuit: '', billing_email: '',
        billing_phone_country_code: '+', billing_phone_area_code: '', billing_phone_number: '',
        company_tax_status_id: '', send_welcome_email: true
    };

    const formInitialValues = initialData ? { ...defaultValues, ...initialData } : defaultValues;

    // Ajustar schema para edición (password opcional)
    const finalSchema = mode === 'edit' 
        ? validationSchema.shape({ password: Yup.string().min(6, 'Mínimo 6 caracteres si la cambias') })
        : validationSchema.shape({ password: Yup.string().required('La contraseña es obligatoria').min(6, 'Mínimo 6 caracteres') });

    if (loadingOptions) {
        return <div className="d-flex justify-content-center p-5"><CSpinner color="primary" /></div>;
    }

    return (
        <>
            <CustomBreadcrumbs parentTitle={'Empresas'} activeTitle={breadcrumbTitle} /> 
            <CContainer>
                <div className='mb-4'><h2 className='title'>{pageTitle}</h2></div>
                <CCard className="mb-4">
                    <CCardBody>
                        <Formik
                            initialValues={formInitialValues}
                            validationSchema={finalSchema}
                            onSubmit={handleSubmit}
                            enableReinitialize={true} // Importante para cuando carga initialData asíncrono
                        >
                        {({ isSubmitting, handleChange, handleBlur, values, touched, errors }) => (
                            <Form>
                                {/* --- Sección 1: Datos Principales --- */}
                                <CRow className="mb-3">
                                    <CCol md={6}>
                                        <CFormLabel>Nombre de la Compañía *</CFormLabel>
                                        <CFormInput name="company_name" value={values.company_name} onChange={handleChange} onBlur={handleBlur} invalid={touched.company_name && !!errors.company_name} />
                                        <CFormFeedback invalid>{errors.company_name}</CFormFeedback>
                                    </CCol>
                                    <CCol md={6}>
                                        <CFormLabel>Nombre del Dueño *</CFormLabel>
                                        <CFormInput name="owner_name" value={values.owner_name} onChange={handleChange} onBlur={handleBlur} invalid={touched.owner_name && !!errors.owner_name} />
                                        <CFormFeedback invalid>{errors.owner_name}</CFormFeedback>
                                    </CCol>
                                </CRow>

                                <CRow className="mb-3">
                                    <CCol md={6}>
                                        <CFormLabel>Email Corporativo *</CFormLabel>
                                        <CFormInput type="email" name="email" value={values.email} onChange={handleChange} onBlur={handleBlur} invalid={touched.email && !!errors.email} />
                                        <CFormFeedback invalid>{errors.email}</CFormFeedback>
                                    </CCol>
                                    <CCol md={6}>
                                        <CFormLabel>Contraseña {mode === 'edit' && '(Dejar vacío si no cambia)'}</CFormLabel>
                                        <CInputGroup>
                                            <CFormInput type={showPassword ? "text" : "password"} name="password" value={values.password} onChange={handleChange} onBlur={handleBlur} invalid={touched.password && !!errors.password} placeholder={mode === 'edit' ? "Nueva contraseña" : "Mínimo 6 caracteres"} />
                                            <CInputGroupText onClick={() => setShowPassword(!showPassword)} style={{ cursor: 'pointer', borderTopRightRadius: '0.375rem', borderBottomRightRadius: '0.375rem', borderLeft: 'none' }}>
                                                {showPassword ? (
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16"><path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z"/><path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z"/><path d="M3.35 5.47c-.18.16-.353.322-.518.487A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884-12-12 .708-.708 12 12-.708.708z"/></svg>
                                                ) : (
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16"><path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"/><path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.817 8a3.178 3.178 0 0 1 .654-1.924A3.48 3.48 0 0 1 8 5c.932 0 1.786.376 2.41.992A3.178 3.178 0 0 1 11.183 8a3.178 3.178 0 0 1-.654 1.924A3.48 3.48 0 0 1 8 11c-.932 0-1.786-.376-2.41-.992A3.177 3.177 0 0 1 4.817 8z"/></svg>
                                                )}
                                            </CInputGroupText>
                                            <CFormFeedback invalid>{errors.password}</CFormFeedback>
                                        </CInputGroup>
                                    </CCol>
                                </CRow>

                                <CRow className="mb-3">
                                    <CCol md={4}>
                                        <CFormLabel>Categoría *</CFormLabel>
                                        <CFormSelect name="company_category_id" value={values.company_category_id} onChange={handleChange} onBlur={handleBlur} invalid={touched.company_category_id && !!errors.company_category_id}>
                                            <option value="">Seleccione</option>
                                            {companyCategories.map((cat) => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                                        </CFormSelect>
                                        <CFormFeedback invalid>{errors.company_category_id}</CFormFeedback>
                                    </CCol>
                                    <CCol md={4}>
                                        <CFormLabel>Plan Suscripción *</CFormLabel>
                                        <CFormSelect name="subscription_plan_id" value={values.subscription_plan_id} onChange={handleChange} onBlur={handleBlur} invalid={touched.subscription_plan_id && !!errors.subscription_plan_id}>
                                            <option value="">Seleccione</option>
                                            {companySubscriptionPlans.map((plan) => <option key={plan.id} value={plan.id}>{plan.name}</option>)}
                                        </CFormSelect>
                                        <CFormFeedback invalid>{errors.subscription_plan_id}</CFormFeedback>
                                    </CCol>
                                    <CCol md={4}>
                                        <CFormLabel>Estado Suscripción *</CFormLabel>
                                        <CFormSelect name="subscription_status_id" value={values.subscription_status_id} onChange={handleChange} onBlur={handleBlur} invalid={touched.subscription_status_id && !!errors.subscription_status_id}>
                                            <option value="">Seleccione</option>
                                            {companySubscriptionStatuses.map((st) => <option key={st.id} value={st.id}>{st.name}</option>)}
                                        </CFormSelect>
                                        <CFormFeedback invalid>{errors.subscription_status_id}</CFormFeedback>
                                    </CCol>
                                </CRow>

                                <hr className="my-4" />
                                <h5>Contacto Principal</h5>
                                <CRow className="mb-3">
                                    <CCol md={4}><CFormLabel>Código País</CFormLabel><CFormInput name="phone_country_code" value={values.phone_country_code} onChange={handleChange} onBlur={handleBlur} invalid={touched.phone_country_code && !!errors.phone_country_code} /></CCol>
                                    <CCol md={4}><CFormLabel>Código Área</CFormLabel><CFormInput name="phone_area_code" value={values.phone_area_code} onChange={handleChange} onBlur={handleBlur} invalid={touched.phone_area_code && !!errors.phone_area_code} /></CCol>
                                    <CCol md={4}><CFormLabel>Teléfono</CFormLabel><CFormInput name="phone_number" value={values.phone_number} onChange={handleChange} onBlur={handleBlur} invalid={touched.phone_number && !!errors.phone_number} /></CCol>
                                </CRow>

                                <hr className="my-4" />
                                <h5>Datos de Facturación (Opcional)</h5>
                                <CRow className="mb-3">
                                    <CCol md={6}><CFormLabel>Nombre Facturación</CFormLabel><CFormInput name="billing_name" value={values.billing_name} onChange={handleChange} onBlur={handleBlur} invalid={touched.billing_name && !!errors.billing_name} /></CCol>
                                    <CCol md={6}><CFormLabel>CUIT</CFormLabel><CFormInput name="billing_cuit" value={values.billing_cuit} onChange={handleChange} onBlur={handleBlur} invalid={touched.billing_cuit && !!errors.billing_cuit} /></CCol>
                                </CRow>
                                <CRow className="mb-3">
                                    <CCol md={6}><CFormLabel>Email Facturación</CFormLabel><CFormInput type="email" name="billing_email" value={values.billing_email} onChange={handleChange} onBlur={handleBlur} invalid={touched.billing_email && !!errors.billing_email} /></CCol>
                                    <CCol md={6}><CFormLabel>Condición IVA</CFormLabel><CFormSelect name="company_tax_status_id" value={values.company_tax_status_id} onChange={handleChange} onBlur={handleBlur} invalid={touched.company_tax_status_id && !!errors.company_tax_status_id}>
                                        <option value="">Seleccione</option>
                                        {companyTaxStatuses.map((tax) => <option key={tax.id} value={tax.id}>{tax.name}</option>)}
                                    </CFormSelect></CCol>
                                </CRow>
                                <CRow className="mb-3">
                                    <CCol md={4}><CFormLabel>Cód. País (Fact.)</CFormLabel><CFormInput name="billing_phone_country_code" value={values.billing_phone_country_code} onChange={handleChange} onBlur={handleBlur} invalid={touched.billing_phone_country_code && !!errors.billing_phone_country_code} /></CCol>
                                    <CCol md={4}><CFormLabel>Cód. Área (Fact.)</CFormLabel><CFormInput name="billing_phone_area_code" value={values.billing_phone_area_code} onChange={handleChange} onBlur={handleBlur} invalid={touched.billing_phone_area_code && !!errors.billing_phone_area_code} /></CCol>
                                    <CCol md={4}><CFormLabel>Teléfono (Fact.)</CFormLabel><CFormInput name="billing_phone_number" value={values.billing_phone_number} onChange={handleChange} onBlur={handleBlur} invalid={touched.billing_phone_number && !!errors.billing_phone_number} /></CCol>
                                </CRow>
                                {mode === 'create' && (
                                    <>
                                    <hr className="my-4" />
                                    <CRow className="mb-3">
                                        <CCol md={4}>
                                            <CFormCheck id="sendWelcomeEmail" name="send_welcome_email" value={values.send_welcome_email} onChange={handleChange} onBlur={handleBlur} invalid={touched.send_welcome_email && !!errors.send_welcome_email} label="Enviar correo de bienvenida" defaultChecked />
                                        </CCol>
                                    </CRow>
                                    </>
                                )}

                                <div className="d-grid gap-2 d-md-flex justify-content-md-end mt-4">
                                    <CButton color="secondary" onClick={() => navigate(-1)} disabled={isSubmittingGlobal}>Cancelar</CButton>
                                    <CButton color="primary" type="submit" disabled={isSubmittingGlobal || isSubmitting} className="btn-custom-yellow">
                                        {(isSubmittingGlobal || isSubmitting) && <CSpinner size="sm" className="me-2" />}
                                        {mode === 'create' ? 'Crear Compañía' : 'Guardar Cambios'}
                                    </CButton>
                                </div>
                                
                                {apiError && <CAlert color="danger" className="mt-4" dismissible onClose={() => setApiError(null)}>{apiError}</CAlert>}
                                {apiSuccess && <CAlert color="success" className="mt-4" dismissible onClose={() => setApiSuccess(null)}>{apiSuccessMessage}</CAlert>}
                            </Form>
                        )}
                        </Formik>
                    </CCardBody>
                </CCard>
            </CContainer>
        </>
    );
};

export default FormCompany;